import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { auth, functions } from "@/lib/firebase";
import { resendVerificationEmail } from "@/lib/auth";
import { httpsCallable } from "firebase/functions";
import { reload } from "firebase/auth";
import { Logo } from "@/components/shared/Logo";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";

export function EmailSent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isResending, setIsResending] = useState(false);
  const user = auth.currentUser;

  // Check verification status periodically
  useEffect(() => {
    if (!user) return;

    const checkVerification = async () => {
      try {
        // Reload user to get latest verification status
        await reload(user);
        const currentUser = auth.currentUser;
        
        if (currentUser?.emailVerified) {
          // Call Cloud Function to update Firestore
          try {
            const updateEmailVerification = httpsCallable(functions, "onEmailVerified");
            await updateEmailVerification();
            console.log("✓ Email verification updated via Cloud Function");
          } catch (error) {
            console.error("Error calling Cloud Function:", error);
          }
          
          toast({
            title: t("emailSent.verified") || "Email Verified!",
            description: t("emailSent.verifiedMessage") || "Your email has been verified successfully.",
          });
        }
      } catch (error) {
        console.error("Error checking verification:", error);
      }
    };

    // Check immediately
    checkVerification();

    // Check every 5 seconds
    const interval = setInterval(checkVerification, 5000);

    return () => clearInterval(interval);
  }, [user, toast, t]);

  const handleResend = async () => {
    if (!user) return;
    setIsResending(true);
    try {
      await resendVerificationEmail(user);
      toast({
        title: t("emailSent.resendSuccess"),
      });
    } catch (error: any) {
      toast({
        title: t("errors.default"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container flex items-center justify-center py-8">
        <div className="w-full max-w-md space-y-8">
          <Logo />
          <Card className="w-full shadow-lg border-primary/10">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center text-primary">
                {t("emailSent.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="border-primary/20 bg-primary/5">
                <AlertDescription>
                  <p>
                    {t("emailSent.message")} <strong>{user?.email}</strong>
                  </p>
                </AlertDescription>
              </Alert>

              <p className="text-sm text-muted-foreground">
                {t("emailSent.instructions")}
              </p>

              <p className="text-xs text-muted-foreground">
                {t("emailSent.checkSpam")}
              </p>

              {user && (
                <Button
                  onClick={handleResend}
                  variant="outline"
                  className="w-full"
                  disabled={isResending}
                >
                  {isResending ? t("common.loading") : t("emailSent.resendEmail")}
                </Button>
              )}

              <div className="pt-4 space-y-2">
                <p className="text-sm text-muted-foreground text-center">
                  {t("emailSent.returnInstructions")}
                </p>
                <Button
                  onClick={() => navigate("/login")}
                  className="w-full bg-gradient-to-r from-primary to-secondary hover:opacity-90"
                >
                  {t("emailSent.returnToApp")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
